'use client';
import { useEffect, useState } from 'react';
import { supabaseBrowser } from '@/lib/supabaseBrowser';

const DISMISS_KEY = 'ng-install-prompt-dismissed';

export default function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    // Fires on Android/Chrome the moment an install actually completes,
    // regardless of whether it was triggered by our own button or the
    // browser's own install UI -- so this is the one true signal, not
    // something to also log separately inside handleAndroidInstall (that
    // would double-count the same install). No iOS equivalent exists at
    // all -- Safari's Add to Home Screen has no JS hook whatsoever, so
    // this can only ever capture Android installs.
    const onInstalled = () => {
      (async () => {
        const supabase = supabaseBrowser();
        const { data: { session } } = await supabase.auth.getSession();
        fetch('/api/log-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ eventType: 'pwa_installed', metadata: { platform: 'android' } }),
        }).catch(() => {});
      })();
    };
    window.addEventListener('appinstalled', onInstalled);

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // iOS has no 'appinstalled' event at all -- WebKit simply doesn't expose
    // one. This is the best available proxy: display-mode:standalone only
    // ever becomes true once someone actually launches from a home-screen
    // icon, so the FIRST time a given browser shows up in standalone mode
    // is a reasonable stand-in for "recently installed." Not as precise as
    // Android's real event (could theoretically fire for an old install if
    // this code shipped after they'd already added it), but it's genuinely
    // the only signal iOS exposes at all.
    if (isStandalone && !localStorage.getItem('ng-seen-standalone')) {
      localStorage.setItem('ng-seen-standalone', '1');
      (async () => {
        const supabase = supabaseBrowser();
        const { data: { session } } = await supabase.auth.getSession();
        fetch('/api/log-event', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(session ? { Authorization: `Bearer ${session.access_token}` } : {}),
          },
          body: JSON.stringify({ eventType: 'pwa_installed', metadata: { platform: 'ios', method: 'standalone_proxy' } }),
        }).catch(() => {});
      })();
    }

    // Already installed? Never show this at all — standalone display mode
    // is the reliable cross-platform way to detect that.
    if (isStandalone) return () => window.removeEventListener('appinstalled', onInstalled);

    // Previously dismissed? Respect it — this should never feel naggy.
    if (localStorage.getItem(DISMISS_KEY)) return () => window.removeEventListener('appinstalled', onInstalled);

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isAndroid = /Android/.test(navigator.userAgent);

    if (isIOS) {
      setPlatform('ios');
      // Wait a bit before showing anything — appearing instantly on someone's
      // very first page load reads as an ad, not a helpful nudge.
      const t = setTimeout(() => setVisible(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }
    if (isAndroid) {
      setPlatform('android');
      // Chrome fires this when it decides the site is a genuinely good
      // install candidate. Capturing it lets us show our own tasteful UI
      // and trigger the real native prompt on click, instead of Chrome's
      // own default mini-infobar.
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e);
        setTimeout(() => setVisible(true), 4000);
      };
      window.addEventListener('beforeinstallprompt', handler);
      return () => {
        window.removeEventListener('beforeinstallprompt', handler);
        window.removeEventListener('appinstalled', onInstalled);
      };
    }

    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  function dismiss() {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, '1');
  }

  async function handleAndroidInstall() {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    // Not logging pwa_installed here on purpose -- the 'appinstalled' listener
    // above already catches the actual install a moment later. Logging here
    // too would double-count, since 'appinstalled' fires either way once the
    // browser confirms it, not conditionally on how it was triggered.
    setDeferredPrompt(null);
    dismiss();
  }

  if (!visible || !platform) return null;

  return (
    <div style={{
      position: 'fixed', bottom: '16px', left: '16px', right: '16px',
      maxWidth: '420px', margin: '0 auto', zIndex: 50,
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
      padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px',
      boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
    }}>
      <div style={{ fontSize: '26px', flexShrink: 0 }}>🌿</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', marginBottom: '2px' }}>
          Get NoiseGate on your home screen
        </div>
        <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.5 }}>
          {platform === 'ios'
            ? <>Tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.</>
            : 'One tap — opens full-screen, just like an app.'}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
        {platform === 'android' && (
          <button onClick={handleAndroidInstall} className="btn btn-primary" style={{ fontSize: '12px', padding: '6px 12px' }}>
            Install
          </button>
        )}
        <button
          onClick={dismiss}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)',
            padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M5 5l14 14M19 5L5 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}
